import {IssueCreatePage} from '@github-ui/issue-create/IssueCreatePage'
import type {IssueCreateDiscussionData, IssueCreateMetadataTypes} from '@github-ui/issue-create/TemplateArgs'
import {readIssueTypeFromFragment} from '@github-ui/item-picker/IssueTypePicker'
import {readLabelFromFragment} from '@github-ui/item-picker/LabelPicker'
import {readProjectFromFragment} from '@github-ui/item-picker/ProjectPicker'
import {readAssigneeFromFragment} from '@github-ui/item-picker-assignee-relay'
import {readMilestoneFromFragment} from '@github-ui/item-picker-milestone-relay'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {VALUES} from '../../constants/values'
import type {AppPayload} from '../../types/app-payload'
import type {InternalIssueNewPageUrlArgumentsMetadata$key} from './__generated__/InternalIssueNewPageUrlArgumentsMetadata.graphql'
import type {InternalIssueNewPageUrlArgumentsMetadataQuery} from './__generated__/InternalIssueNewPageUrlArgumentsMetadataQuery.graphql'

export const InternalIssueNewPageUrlArgumentsMetadata = graphql`
  query InternalIssueNewPageUrlArgumentsMetadataQuery(
    $owner: String!
    $name: String!
    $withAssignees: Boolean = false
    $assigneeLogins: String = ""
    $withLabels: Boolean = false
    $labelNames: String = ""
    $withMilestone: Boolean = false
    $milestoneTitle: String = ""
    $type: String = ""
    $withType: Boolean = false
    $withProjects: Boolean = false
    $projectNumbers: [Int!] = []
    $withTriagePermission: Boolean = false
    $discussionNumber: Int = 0
    $includeDiscussion: Boolean = false
    $templateFilter: String = ""
    $withTemplate: Boolean = false
  ) {
    repository(owner: $owner, name: $name) {
      ...InternalIssueNewPageUrlArgumentsMetadata
        @arguments(
          withAssignees: $withAssignees
          assigneeLogins: $assigneeLogins
          withLabels: $withLabels
          labelNames: $labelNames
          withMilestone: $withMilestone
          milestoneTitle: $milestoneTitle
          type: $type
          withType: $withType
          withProjects: $withProjects
          projectNumbers: $projectNumbers
          withTriagePermission: $withTriagePermission
          discussionNumber: $discussionNumber
          includeDiscussion: $includeDiscussion
          templateFilter: $templateFilter
          withTemplate: $withTemplate
        )
    }
  }
`

export type InternalIssueNewPageProps = {
  urlParameterQueryData: PreloadedQuery<InternalIssueNewPageUrlArgumentsMetadataQuery>
}

export const InternalIssueNewPageWithUrlParams = ({urlParameterQueryData}: InternalIssueNewPageProps) => {
  const repositoryData = usePreloadedQuery<InternalIssueNewPageUrlArgumentsMetadataQuery>(
    InternalIssueNewPageUrlArgumentsMetadata,
    urlParameterQueryData,
  )?.repository

  if (!repositoryData) {
    return <div>Repository not found</div>
  }
  return <InternalIssueNewPageWithUrlParamsInternal repository={repositoryData} />
}

const InternalIssueNewPageWithUrlParamsInternal = ({
  repository,
}: {
  repository: InternalIssueNewPageUrlArgumentsMetadata$key
}) => {
  const appPayload = useAppPayload<AppPayload>()

  const repositoryData = useFragment(
    graphql`
      fragment InternalIssueNewPageUrlArgumentsMetadata on Repository
      @argumentDefinitions(
        withAssignees: {type: "Boolean!", defaultValue: false}
        assigneeLogins: {type: "String!", defaultValue: ""}
        withLabels: {type: "Boolean!", defaultValue: false}
        labelNames: {type: "String!", defaultValue: ""}
        withMilestone: {type: "Boolean!", defaultValue: false}
        milestoneTitle: {type: "String!", defaultValue: ""}
        type: {type: "String!", defaultValue: ""}
        withType: {type: "Boolean!", defaultValue: false}
        withProjects: {type: "Boolean!", defaultValue: false}
        projectNumbers: {type: "[Int!]!", defaultValue: []}
        withTriagePermission: {type: "Boolean!", defaultValue: false}
        discussionNumber: {type: "Int", defaultValue: 0}
        includeDiscussion: {type: "Boolean!", defaultValue: false}
        templateFilter: {type: "String!", defaultValue: ""}
        withTemplate: {type: "Boolean!", defaultValue: false}
      ) {
        ...IssueCreatePage @arguments(templateFilter: $templateFilter, withTemplate: $withTemplate)
        viewerIssueCreationPermissions {
          assignable @include(if: $withAssignees)
          labelable @include(if: $withLabels)
          milestoneable @include(if: $withMilestone)
          triageable @include(if: $withTriagePermission)
          typeable @include(if: $withType)
        }
        suggestedActors(capabilities: [CAN_BE_ASSIGNED], first: 10, loginNames: $assigneeLogins)
          @include(if: $withAssignees) {
          nodes {
            # eslint-disable-next-line relay/must-colocate-fragment-spreads
            ...AssigneePickerSharedAssignee
          }
        }
        labels(first: 20, names: $labelNames) @include(if: $withLabels) {
          nodes {
            ...LabelPickerLabel
            # eslint-disable-next-line relay/must-colocate-fragment-spreads
            ...ItemPickerLabelItem
          }
        }
        discussion(number: $discussionNumber) @include(if: $includeDiscussion) {
          formattedBody
          title
          labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
            edges {
              node {
                ...LabelPickerLabel
                # eslint-disable-next-line relay/must-colocate-fragment-spreads
                ...ItemPickerLabelItem
              }
            }
          }
        }
        milestoneByTitle(title: $milestoneTitle) @include(if: $withMilestone) {
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...ItemPickerMilestoneRelayMilestone
        }
        issueType(name: $type) @include(if: $withType) {
          ...IssueTypePickerIssueType
          ...IssueTypePickerIssueTypeShared
        }
        owner {
          ... on ProjectV2Owner {
            projectsV2ByNumber(first: 20, numbers: $projectNumbers) @include(if: $withProjects) {
              nodes {
                ...ProjectPickerProject
                ...ProjectPickerRelay
              }
            }
          }
        }
      }
    `,
    repository,
  )

  const assigneeNodes = repositoryData?.suggestedActors?.nodes
  const assignees =
    assigneeNodes && repositoryData.viewerIssueCreationPermissions?.assignable
      ? assigneeNodes.flatMap(e => (e ? [readAssigneeFromFragment(e)] : []))
      : undefined

  const labelNodes = repositoryData?.labels?.nodes
  const labels =
    labelNodes && repositoryData.viewerIssueCreationPermissions?.labelable
      ? labelNodes.flatMap(e => (e ? [readLabelFromFragment(e)] : []))
      : undefined

  const projectNodes = repositoryData?.owner?.projectsV2ByNumber?.nodes
  const projects =
    projectNodes && repositoryData.viewerIssueCreationPermissions?.triageable
      ? projectNodes.flatMap(e => (e ? [readProjectFromFragment(e)] : []))
      : undefined

  const milestone =
    repositoryData?.milestoneByTitle && repositoryData.viewerIssueCreationPermissions?.milestoneable
      ? readMilestoneFromFragment(repositoryData?.milestoneByTitle)
      : undefined

  const issueType =
    repositoryData?.issueType &&
    repositoryData.viewerIssueCreationPermissions?.triageable &&
    repositoryData.viewerIssueCreationPermissions?.typeable
      ? readIssueTypeFromFragment(repositoryData?.issueType)
      : undefined

  const discussion = repositoryData?.discussion
    ? ({
        title: repositoryData.discussion.title,
        formattedBody: repositoryData.discussion.formattedBody,
        labels: repositoryData.discussion.labels?.edges?.flatMap(e =>
          e?.node ? [readLabelFromFragment(e?.node)] : [],
        ),
      } as IssueCreateDiscussionData)
    : undefined

  const initialMetadataValues: IssueCreateMetadataTypes = {
    ...(assignees !== undefined && {assignees}),
    ...(labels !== undefined && {labels}),
    ...(milestone !== undefined && {milestone}),
    ...(projects !== undefined && {projects}),
    ...(issueType !== undefined && {type: issueType}),
    ...(discussion !== undefined && {discussion}),
  }
  const storageKeyPrefix = VALUES.storageKeyPrefix(appPayload)
  const pasteUrlsAsPlainText = appPayload?.current_user_settings?.paste_url_link_as_plain_text || false
  const useMonospaceFont = appPayload?.current_user_settings?.use_monospace_font || false
  const emojiSkinTonePreference = appPayload?.current_user_settings?.preferred_emoji_skin_tone
  const singleKeyShortcutsEnabled = appPayload?.current_user_settings?.use_single_key_shortcut || false
  const copilotShowFunctionality = appPayload?.current_user_settings?.copilot_show_functionality || false

  return (
    <IssueCreatePage
      initialMetadataValues={initialMetadataValues}
      storageKeyPrefix={storageKeyPrefix}
      pasteUrlsAsPlainText={pasteUrlsAsPlainText}
      useMonospaceFont={useMonospaceFont}
      emojiSkinTonePreference={emojiSkinTonePreference}
      singleKeyShortcutsEnabled={singleKeyShortcutsEnabled}
      copilotShowFunctionality={copilotShowFunctionality}
      currentRepository={repositoryData}
    />
  )
}

try{ InternalIssueNewPageWithUrlParams.displayName ||= 'InternalIssueNewPageWithUrlParams' } catch {}
try{ InternalIssueNewPageWithUrlParamsInternal.displayName ||= 'InternalIssueNewPageWithUrlParamsInternal' } catch {}